import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { IconBuilding, IconLogout, IconSearch, IconUser, IconUserShield } from '@tabler/icons-react';
import { apiService } from '../services/api';
import './TenantSelectModal.css';

interface Tenant {
    id: number;
    code: string;
    companyName: string;
    own: boolean;
}

interface TenantUser {
    id: number;
    email: string;
    /** Flowy admin (may enter the admin app). */
    admin: boolean;
    clientAdmin: boolean;
    /** Names of the user's groups - where their rights come from. */
    groups: string[];
}

interface TenantSelectModalProps {
    show: boolean;
    /** False right after login, when the admin has to pick before working; true when switching later. */
    canClose: boolean;
    currentTenantName?: string;
    onChosen: (token: string) => void;
    onClose: () => void;
    onLogout: () => void;
}

/**
 * A full admin chooses how to work - right after login and again from the
 * sidebar: normally, as themselves in their own tenant, or in a customer's
 * tenant as one of its users. The API hands back a token for that choice.
 */
export const TenantSelectModal: React.FC<TenantSelectModalProps> = ({ show, canClose, currentTenantName, onChosen, onClose, onLogout }) => {
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [query, setQuery] = useState('');
    const [picked, setPicked] = useState<Tenant | null>(null);
    const [users, setUsers] = useState<TenantUser[]>([]);
    const [selecting, setSelecting] = useState<string | null>(null);

    useEffect(() => {
        if (!show) return;
        let alive = true;
        setLoading(true);
        setError(null);
        setQuery('');
        setPicked(null);
        apiService.getTenants().then((res) => {
            if (!alive) return;
            if (res.success) {
                setTenants(res.data?.data ?? []);
            } else {
                setError(res.data?.message || 'Zoznam zákazníkov sa nepodarilo načítať.');
            }
            setLoading(false);
        });
        return () => { alive = false; };
    }, [show]);

    const filteredTenants = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return needle
            ? tenants.filter(t => t.companyName.toLowerCase().includes(needle) || t.code.toLowerCase().includes(needle))
            : tenants;
    }, [tenants, query]);

    /** The tenant the admin's own account belongs to: working there is working normally, as themselves. */
    const own = useMemo(() => tenants.find(t => t.own) ?? null, [tenants]);

    const filteredUsers = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return needle
            ? users.filter(u => u.email.toLowerCase().includes(needle) || u.groups.some(g => g.toLowerCase().includes(needle)))
            : users;
    }, [users, query]);

    const pick = async (tenant: Tenant) => {
        setPicked(tenant);
        setQuery('');
        setError(null);
        setUsers([]);
        setLoading(true);
        const res = await apiService.getTenantUsers(tenant.id);
        if (res.success) {
            setUsers(res.data?.data ?? []);
        } else {
            setError(res.data?.message || 'Zoznam používateľov sa nepodarilo načítať.');
        }
        setLoading(false);
    };

    const choose = async (tenant: Tenant, user?: TenantUser) => {
        setSelecting(user ? `user:${user.id}` : 'me');
        setError(null);
        const res = await apiService.selectTenant(tenant.id, user?.id);
        const token = res.success ? res.data?.data?.token : null;
        if (token) {
            onChosen(token);
            return;
        }
        setError(res.data?.message || 'Výber sa nepodaril.');
        setSelecting(null);
    };

    return (
        <Modal show={show} onHide={canClose ? onClose : undefined} centered backdrop="static" keyboard={canClose} className="modal-blur" size={picked ? 'lg' : undefined}>
            <Modal.Header closeButton={canClose}>
                <Modal.Title>{picked ? 'Vyberte používateľa' : 'Vyberte zákazníka'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-secondary mb-3">
                    {picked
                        ? <>Zákazník <strong>{picked.companyName}</strong>. Zvoľte, ako kto chcete pracovať.</>
                        : canClose && currentTenantName
                            ? <>Teraz pracujete ako <strong>{currentTenantName}</strong>. Zvoľte iného zákazníka.</>
                            : 'Ste prihlásený ako administrátor. Pokračujte normálne, alebo zvoľte zákazníka a používateľa, za ktorého chcete pracovať.'}
                </p>

                {error && <div className="alert alert-danger py-2">{error}</div>}

                <div className="input-icon mb-3">
                    <span className="input-icon-addon"><IconSearch size={16}/></span>
                    <input
                        type="search"
                        className="form-control"
                        placeholder={picked ? 'Hľadať používateľa alebo skupinu' : 'Hľadať zákazníka'}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        autoFocus
                    />
                </div>

                {picked ? (
                    <div className="list-group list-group-flush tenant-select-list tenant-select-list--users">
                        {loading && <div className="text-secondary text-center py-3">Načítavam…</div>}
                        {!loading && users.length > 0 && filteredUsers.length === 0 && <div className="text-secondary text-center py-3">Žiadny používateľ nezodpovedá hľadaniu.</div>}
                        {filteredUsers.map((user) => (
                            <button
                                key={user.id}
                                type="button"
                                className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                                disabled={selecting !== null}
                                onClick={() => choose(picked, user)}
                                data-e2e={`tenant-user:${user.id}`}
                            >
                                <span className="tenant-select-icon text-secondary"><IconUser size={18}/></span>
                                <span className="flex-fill text-truncate">
                                    <span className="d-block fw-semibold text-truncate">{user.email}</span>
                                    <small className="d-block text-secondary text-wrap">{user.groups.length > 0 ? user.groups.join(', ') : 'bez skupiny'}</small>
                                </span>
                                {user.admin && <span className="badge bg-danger-lt">admin</span>}
                                {user.clientAdmin && <span className="badge bg-primary-lt">klient admin</span>}
                                {selecting === `user:${user.id}` && <span className="spinner-border spinner-border-sm text-secondary" role="status"/>}
                            </button>
                        ))}
                    </div>
                ) : (
                    <div className="list-group list-group-flush tenant-select-list">
                        {own && (
                            <button
                                type="button"
                                className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                                disabled={selecting !== null}
                                onClick={() => choose(own)}
                                data-e2e="tenant:own"
                            >
                                <span className="tenant-select-icon text-primary"><IconUserShield size={18}/></span>
                                <span className="flex-fill text-truncate">
                                    <span className="d-block fw-semibold text-truncate">Pokračovať normálne</span>
                                    <small className="text-secondary">{own.companyName}</small>
                                </span>
                                {selecting === 'me' && <span className="spinner-border spinner-border-sm text-secondary" role="status"/>}
                            </button>
                        )}
                        {loading && <div className="text-secondary text-center py-3">Načítavam…</div>}
                        {!loading && filteredTenants.length === 0 && <div className="text-secondary text-center py-3">Žiadny zákazník nezodpovedá hľadaniu.</div>}
                        {filteredTenants.map((tenant) => (
                            <button
                                key={tenant.id}
                                type="button"
                                className="list-group-item list-group-item-action d-flex align-items-center gap-3"
                                onClick={() => pick(tenant)}
                                data-e2e={`tenant:${tenant.id}`}
                            >
                                <span className={`tenant-select-icon${tenant.own ? ' text-primary' : ' text-secondary'}`}><IconBuilding size={18}/></span>
                                <span className="flex-fill text-truncate">
                                    <span className="d-block fw-semibold text-truncate">{tenant.companyName}</span>
                                    <small className="text-secondary">{tenant.code}</small>
                                </span>
                                {tenant.own && <span className="badge bg-primary-lt">váš</span>}
                            </button>
                        ))}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <button type="button" className="btn btn-link text-secondary px-2 ms-n2" onClick={onLogout}>
                    <IconLogout size={14} className="me-1"/> Odhlásiť sa
                </button>
                <span className="d-flex gap-2">
                    {picked && (
                        <button type="button" className="btn btn-white btn-pill" disabled={selecting !== null} onClick={() => { setPicked(null); setQuery(''); setError(null); }}>Späť na zákazníkov</button>
                    )}
                    {canClose && (
                        <button type="button" className="btn btn-white btn-pill" onClick={onClose}>Zrušiť</button>
                    )}
                </span>
            </Modal.Footer>
        </Modal>
    );
};
